import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { EXPIRY_BUCKETS, collectionRate, endOfMonth, startOfMonth, type ExpiryBucket } from "@rentorng/core";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useLocalPage } from "@/lib/list-params";
import { Pagination } from "@/components/list-toolbar";
import { BilledVsCollected, MeterBar } from "@/components/chart";
import { IconAlert, IconBell, IconCheck, IconClose, IconDoor, IconWallet } from "@/components/icons";
import { Badge, Card, CardTitle, EmptyState, ErrorState, LinkButton, Spinner, StatTile, Table } from "@/components/ui";
import { usePageTitle } from "@/lib/title";
import { GettingStartedSummary } from "@/components/walkthrough";

/**
 * The dashboard — "the traffic light".
 *
 * Every number here is a link into the list behind it. That is the rule the whole screen is
 * built around: a manager who can see an arrear but cannot open it has been told off, not helped.
 */

interface DashboardData {
  money: {
    overdue: { count: number; amount: number };
    dueSoon: { count: number; amount: number };
    upcoming: { count: number; amount: number };
    outstanding: number;
  };
  collectedThisMonth: number;
  billedThisMonth: number;
  expiries: Record<ExpiryBucket, number>;
  expiringLeases: Array<{
    id: string;
    reference: string;
    endDate: string | null;
    tenantName: string;
    unitLabel: string;
    propertyName: string;
    bucket: ExpiryBucket;
  }>;
  occupancy: { total: number; occupied: number; vacant: number; notice: number; unavailable: number; rate: number };
  unitCount: number;
  propertyCount: number;
  tenantCount: number;
  actions: Array<{ kind: string; href: string; title: string; detail: string; amount?: number }>;
  collections: Array<{ key: string; billed: number; collected: number }>;
  recentPayments: Array<{ id: string; receiptNumber: string; amount: number; receivedOn: string; partyName: string; method: string }>;
  openMaintenance: number;
  overdueMaintenance: number;
  today: string;
}

const BUCKET_LABEL: Record<ExpiryBucket, string> = {
  expired: "Expired",
  d0_30: "≤30 days",
  d31_60: "31–60 days",
  d61_90: "61–90 days",
  later: "Later",
};

/**
 * "Your agency is set up."
 *
 * Sign-up used to end on an empty dashboard, which reads like nothing happened — the one moment
 * somebody needs telling that it worked. `?welcome=1` is set once, by the sign-up form, and
 * cleared the moment it is dismissed or the page is left, so it cannot be come back to.
 *
 * Three links, not a tour. What is missing on day one is a property, somebody to put in it, and
 * the agency's own details on the paperwork; anything more is a page to read instead of use.
 */
function WelcomeBanner() {
  const [search, setSearch] = useSearchParams();
  const { org, vocab } = useSession();
  if (search.get("welcome") !== "1") return null;

  const dismiss = () => {
    const next = new URLSearchParams(search);
    next.delete("welcome");
    setSearch(next, { replace: true });
  };

  return (
    <div className="mb-5 flex flex-wrap items-start gap-4 rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50 p-4">
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-600 text-white">
        <IconCheck size={18} />
      </span>

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-brand-900">{org?.name ?? "Your agency"} is ready.</p>
        <p className="mt-0.5 text-sm text-brand-800">Your account is set up. A good place to start:</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <LinkButton to="/app/properties/new" size="sm" need="property.edit">
            Add a {vocab.property}
          </LinkButton>
          <LinkButton to="/app/clients/new" variant="secondary" size="sm" need="party.edit">
            Add a client
          </LinkButton>
          <LinkButton to="/app/settings" variant="secondary" size="sm" need="org.settings">
            Agency details
          </LinkButton>
        </div>
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-[var(--radius-control)] p-1.5 text-brand-700 hover:bg-brand-100"
      >
        <IconClose size={16} />
      </button>
    </div>
  );
}

/**
 * A subtle inline link: the counts in the greeting line are links, but the line is a sentence
 * rather than a toolbar, so they underline on hover rather than being coloured all the time.
 */
const META_LINK = "rounded underline-offset-2 hover:text-ink-900 hover:underline";

/**
 * Where an action row actually goes.
 *
 * The queue is built by the API, which names two addresses this application does not answer to:
 *
 * - `/app/leases/:id` is the old name for a tenancy. `App.tsx` redirects it, but a middle-click
 *   opens the redirect rather than the record, so it is rewritten here instead of followed.
 * - `/app/maintenance/:id` has no route at all — a job has no page of its own — so the row lands
 *   on the maintenance list searched down to that one job. Its reference is the first thing in
 *   the row's own detail line, which is where this reads it from.
 */
function actionHref(action: DashboardData["actions"][number]): string {
  const lease = /^\/app\/leases\/(.+)$/.exec(action.href);
  if (lease) return `/app/tenancies/${lease[1]}`;
  if (action.kind === "maintenance") {
    const reference = action.detail.split("\u00b7")[0]?.trim();
    return reference ? `/app/maintenance?q=${encodeURIComponent(reference)}` : "/app/maintenance";
  }
  return action.href;
}

export function DashboardPage() {
  usePageTitle("Dashboard");
  const { user, org, vocab } = useSession();
  const fmt = useFormat();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<DashboardData>("/dashboard"),
  });

  // Above the guards: a hook that runs only once the data has arrived runs on some renders and
  // not others, which is the one thing React will not tolerate.
  const actions = useLocalPage(data?.actions ?? [], 5);

  if (isLoading) return <Spinner label="Working out who owes you…" />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  // Deliberately NOT "collected ÷ billed this month". On annual rent most money arriving in
  // August settles an invoice that fell due in March, so that ratio routinely reads 2000% and
  // means nothing. The honest rate is measured across the whole twelve months, on the chart.
  const twelveMonthRate = collectionRate(data.collections.map((point) => ({ ...point, label: point.key })));

  // "Collected this month" is every confirmed payment received inside this calendar month, so the
  // tile opens the payments list with exactly that window on it. Narrowing to confirmed payments
  // is the one part of the tile the list cannot yet express — `listPayments` has no status filter.
  const monthStart = startOfMonth(data.today);
  const monthEnd = endOfMonth(data.today);

  return (
    <div>
      <WelcomeBanner />
      {/*
        Below the welcome, and never beside it.

        `WelcomeBanner` is the one-off "your agency is ready" that appears with `?welcome=1` the
        moment after sign-up; the summary is the standing account of what has actually been done,
        which it reads from the agency's real counts. Both at once is two banners saying "start
        here" stacked on top of each other on somebody's first ever screen, so the summary waits
        until the welcome has been dismissed — one beat later, and the only beat when they overlap.
      */}
      {new URLSearchParams(window.location.search).get("welcome") === "1" ? null : <GettingStartedSummary />}

      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Good to see you, {user?.name.split(" ")[0]}</h1>
          {/* Three counts, three lists. Each is the whole of what it names, so each opens the
              unfiltered list behind it rather than a slice of one. */}
          <p className="mt-1 text-sm text-ink-500">
            {fmt.date(data.today, { long: true })} ·{" "}
            <Link to="/app/properties" className={META_LINK}>
              {data.propertyCount} {vocab.properties}
            </Link>{" "}
            ·{" "}
            <Link to="/app/units" className={META_LINK}>
              {data.unitCount} {vocab.units}
            </Link>{" "}
            ·{" "}
            <Link to="/app/clients?role=tenant" className={META_LINK}>
              {data.tenantCount} {vocab.tenants}
            </Link>
          </p>
        </div>
        <div className="flex gap-2">
          <LinkButton to="/app/tenancies/new" variant="secondary" need="lease.edit">
            New {vocab.lease}
          </LinkButton>
          <LinkButton to="/app/payments/new" need="payment.record">Record a payment</LinkButton>
        </div>
      </div>

      {/* Money at risk — the three numbers the whole product exists to surface. */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Overdue"
          tone="danger"
          icon={<IconAlert size={20} />}
          value={fmt.money(data.money.overdue.amount)}
          sub={`${data.money.overdue.count} ${data.money.overdue.count === 1 ? "invoice" : "invoices"} past due`}
          to="/app/invoices?bucket=overdue"
        />
        <StatTile
          label={`Due in ${org?.dueSoonDays ?? 30} days`}
          tone="warn"
          icon={<IconBell size={20} />}
          value={fmt.money(data.money.dueSoon.amount)}
          sub={`${data.money.dueSoon.count} ${data.money.dueSoon.count === 1 ? "invoice" : "invoices"} falling due`}
          to="/app/invoices?bucket=due_soon"
        />
        <StatTile
          label="Collected this month"
          tone="success"
          icon={<IconWallet size={20} />}
          value={fmt.money(data.collectedThisMonth)}
          sub={`${fmt.money(data.billedThisMonth)} fell due this month`}
          to={`/app/payments?from=${monthStart}&to=${monthEnd}&status=confirmed`}
        />
        <StatTile
          label="Occupancy"
          tone="info"
          icon={<IconDoor size={20} />}
          value={`${data.occupancy.rate}%`}
          sub={`${data.occupancy.occupied} occupied · ${data.occupancy.vacant} vacant`}
          /* The rate is occupied over everything lettable, so the honest destination is the whole
             register — `occupancy.total` and the list's own total are the same number. The states
             behind it are one card down, each opening its own filter. */
          to="/app/units"
        />
      </div>

      {/*
        With nothing to chase, this card is a large empty box in the best position on the page,
        pushing the chart below the fold to say "nothing is wrong". Nothing being wrong is exactly
        when the twelve-month picture is the most useful thing to show, so the card is not rendered
        at all and the chart moves up into its place.
      */}
      <div className="grid gap-5 lg:grid-cols-3">
        {data.actions.length > 0 ? (
        <Card className="lg:col-span-2">
          <CardTitle hint="Overdue money, expiring tenancies and jobs past their response target, ranked.">
            What needs you today
          </CardTitle>

          <ul className="divide-y divide-ink-100">
              {actions.rows.map((action, index) => (
                <li key={`${action.kind}-${action.href}-${index}`}>
                  <Link to={actionHref(action)} className="-mx-2 flex items-center gap-3 rounded px-2 py-2.5 hover:bg-ink-50">
                    <span className="shrink-0">
                      {action.kind === "overdue" ? (
                        <Badge tone="danger" dot>Overdue</Badge>
                      ) : action.kind === "expired" ? (
                        <Badge tone="danger" dot>Expired</Badge>
                      ) : action.kind === "expiring" ? (
                        <Badge tone="warn" dot>Expiring</Badge>
                      ) : (
                        <Badge tone="info" dot>Maintenance</Badge>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink-900">{action.title}</span>
                      <span className="block truncate text-xs text-ink-500">{action.detail}</span>
                    </span>
                    {action.amount ? (
                      <span className="tabular shrink-0 text-sm font-medium text-danger-700">{fmt.money(action.amount)}</span>
                    ) : null}
                  </Link>
                </li>
              ))}
          </ul>

          {/* Five at a time: a dashboard is a glance, and twenty-three rows push everything else
              below the fold. The pager is the same one every list uses. */}
          {data.actions.length > 5 ? (
            <div className="-mx-4 -mb-4 mt-2 sm:-mx-5 sm:-mb-5">
              <Pagination meta={actions.meta} params={actions.params} noun="things to do" />
            </div>
          ) : null}
        </Card>
        ) : (
          /* The chart takes the space instead. Same card, same span — only its row changes. */
          <Card className="lg:col-span-2">
            <CardTitle
              hint={`Grey is what you billed. Green is what actually arrived. ${twelveMonthRate}% collected over twelve months.`}
            >
              Billed against collected
            </CardTitle>
            <BilledVsCollected points={data.collections} format={(value) => fmt.money(value, { compact: true })} />
          </Card>
        )}

        <Card>
          <CardTitle
            hint="Renewals negotiated late cost void weeks."
            action={
              <Link to="/app/tenancies" className="link">
                All {vocab.leases}
              </Link>
            }
          >
            {titleCase(vocab.leases)} expiring
          </CardTitle>

          <div className="space-y-2">
            {EXPIRY_BUCKETS.filter((bucket) => bucket.key !== "later").map((bucket) => (
              <Link
                key={bucket.key}
                to={`/app/tenancies?expiring=${bucket.key}`}
                className="flex items-center justify-between rounded-lg border border-ink-200 px-3 py-2 text-sm hover:bg-ink-50"
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`size-2 rounded-full ${
                      bucket.tone === "danger" ? "bg-danger-500" : bucket.tone === "warn" ? "bg-warn-500" : "bg-info-600"
                    }`}
                  />
                  {BUCKET_LABEL[bucket.key]}
                </span>
                <span className="tabular font-medium">{data.expiries[bucket.key]}</span>
              </Link>
            ))}
          </div>

          {data.expiries.expired + data.expiries.d0_30 > 0 ? (
            <div className="mt-3">
              <LinkButton to="/app/reminders/send?scope=expiring" variant="secondary" size="sm" className="w-full" need="reminder.send">
                Remind everyone expiring
              </LinkButton>
            </div>
          ) : null}
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        {/* Already shown above when there was nothing to chase; drawing it twice would be odd. */}
        {data.actions.length > 0 ? (
          <Card className="lg:col-span-2">
            <CardTitle
              hint={`Grey is what you billed. Green is what actually arrived. ${twelveMonthRate}% collected over twelve months.`}
            >
              Billed against collected
            </CardTitle>
            <BilledVsCollected points={data.collections} format={(value) => fmt.money(value, { compact: true })} />
          </Card>
        ) : (
          <div className="lg:col-span-2" />
        )}

        <div className="space-y-5">
          <Card>
            <CardTitle>Occupancy</CardTitle>
            <MeterBar value={data.occupancy.occupied} max={Math.max(1, data.occupancy.occupied + data.occupancy.vacant)} label="Let" />
            {/*
              A figure links only where a list can show exactly the rows it counted.

              Two of these four can. "Occupied" counts a tenant who has given notice as still in
              the building, so it is `occupancy=occupied` — the one filter that means the same
              thing — and not `status=occupied`, which is a shorter list than the number claims.
              "On notice" is a single status and needs nothing.

              The other two say two things each — "Vacant" is vacant *or* reserved, "Unavailable"
              is under renovation *or* off market — and `/units` used to filter on one status at a
              time, so the nearest link opened a list of 5 under a figure of 6, or the whole
              register under a figure of 1. `listUnits` now takes `occupancy=vacant` and
              `occupancy=unavailable`, which are those exact pairs, so all four agree.
            */}
            <dl className="mt-3 space-y-1.5 text-sm">
              {[
                { label: "Occupied", value: data.occupancy.occupied, to: "/app/units?occupancy=occupied", tone: "" },
                { label: "On notice", value: data.occupancy.notice, to: "/app/units?status=notice", tone: "" },
                { label: "Vacant", value: data.occupancy.vacant, to: "/app/units?occupancy=vacant", tone: "text-danger-700" },
                { label: "Unavailable", value: data.occupancy.unavailable, to: "/app/units?occupancy=unavailable", tone: "" },
              ].map((row) => {
                const body = (
                  <>
                    <dt className="text-ink-500">{row.label}</dt>
                    <dd className={`tabular font-medium ${row.tone}`}>{row.value}</dd>
                  </>
                );
                return row.to ? (
                  <Link
                    key={row.label}
                    to={row.to}
                    className="-mx-2 flex justify-between rounded px-2 py-1 hover:bg-ink-50"
                  >
                    {body}
                  </Link>
                ) : (
                  <div key={row.label} className="-mx-2 flex justify-between px-2 py-1">
                    {body}
                  </div>
                );
              })}
            </dl>
          </Card>

          <Card>
            {/* The figure is the open jobs, so the link is `open=1` and not the whole register.
                `MaintenancePage` used to hold that filter in component state, which made the
                address unanswerable and this figure open a longer list than itself. */}
            <CardTitle action={<Link to="/app/maintenance?open=1" className="link">Open jobs</Link>}>Maintenance</CardTitle>
            <Link to="/app/maintenance?open=1" className="tabular block text-2xl font-semibold hover:text-brand-700">
              {data.openMaintenance}
            </Link>
            <p className="mt-1 text-xs text-ink-500">
              {data.overdueMaintenance > 0 ? (
                <span className="text-danger-700">{data.overdueMaintenance} past their response target</span>
              ) : (
                "All within target"
              )}
            </p>
          </Card>
        </div>
      </div>

      <Card className="mt-5" padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle action={<Link to="/app/payments" className="link">All payments</Link>}>Recent payments</CardTitle>
        </div>

        {data.recentPayments.length === 0 ? (
          <EmptyState title="No payments recorded yet." action={<LinkButton to="/app/payments/new" need="payment.record">Record a payment</LinkButton>} />
        ) : (
          <Table
            head={
              <tr>
                <th>Receipt</th>
                <th>From</th>
                <th>Method</th>
                <th>Received</th>
                <th className="num">Amount</th>
              </tr>
            }
          >
            {data.recentPayments.map((payment) => (
              <tr key={payment.id}>
                <td>
                  <Link to={`/app/payments/${payment.id}`} className="link">
                    {payment.receiptNumber}
                  </Link>
                </td>
                <td>{payment.partyName}</td>
                <td className="capitalize text-ink-500">{payment.method.replace(/_/g, " ")}</td>
                <td className="text-ink-500">{fmt.date(payment.receivedOn)}</td>
                <td className="num tabular font-medium">{fmt.money(payment.amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
